import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  LinearProgress,
  CircularProgress,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FileText as DescriptionIcon,
  Trash2 as DeleteIcon,
  UserPlus as PersonAddIcon,
  CheckCircle2 as CheckCircleIcon,
  AlertCircle as ErrorIcon,
  Wand2 as AutoFixHighIcon,
  ChevronDown as ExpandMoreIcon,
  Pencil as EditIcon,
} from 'lucide-react';

import api from '../../../utils/axios';
import PatientFormDialog from '../../../components/PatientFormDialog';
import {
  createDenialAnalysisFromAppeal,
  getDenialAnalysis,
  submitAnalysis,
} from '../../../utils/denialAnalysisApi';

const PRIORITY_COLORS = { high: 'error', medium: 'warning', low: 'default' };

const ConfidenceBadge = ({ value }) => {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'error';
  return <Chip label={`${pct}% confidence`} color={color} size="small" />;
};

// Inline results display — subset of DenialAnalysis ResultsStep
const InlineDenialResults = ({ result }) => {
  if (!result) return null;
  const {
    is_denial_letter,
    denial_type,
    denial_category,
    payer_name,
    patient_name,
    claim_number,
    date_of_service,
    date_of_denial,
    appeal_deadline,
    denial_codes_raw = [],
    plain_english_explanation,
    appeal_requirements,
    next_steps = [],
    overall_confidence,
    confidence_flags = [],
  } = result;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Denial Analysis Results</Typography>
        <ConfidenceBadge value={overall_confidence} />
      </Box>

      {!is_denial_letter && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This document may not be a denial letter. Results may be limited.
        </Alert>
      )}

      {confidence_flags.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Confidence Flags:</strong>{' '}
          {confidence_flags.map(f => `${f.field}: ${f.note}`).join('; ')}
        </Alert>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Classification</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Type: ${denial_type || 'Unknown'}`} variant="outlined" />
            <Chip label={`Category: ${denial_category || 'Unknown'}`} variant="outlined" />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Claim Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableBody>
              {[
                ['Payer', payer_name],
                ['Patient', patient_name],
                ['Claim Number', claim_number],
                ['Date of Service', date_of_service],
                ['Date of Denial', date_of_denial],
                ['Appeal Deadline', appeal_deadline],
              ].filter(([, v]) => v).map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>{label}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {denial_codes_raw.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
            <Typography fontWeight={600}>Denial Codes ({denial_codes_raw.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {denial_codes_raw.map((code, idx) => (
                  <TableRow key={idx}>
                    <TableCell><strong>{code.code}</strong></TableCell>
                    <TableCell><Chip label={code.type} size="small" /></TableCell>
                    <TableCell>{code.carc_full_text || code.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Plain-English Explanation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ mb: appeal_requirements ? 2 : 0 }}>{plain_english_explanation}</Typography>
          {appeal_requirements && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Appeal Requirements</Typography>
              <Typography>{appeal_requirements}</Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {next_steps.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
            <Typography fontWeight={600}>Next Steps ({next_steps.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {next_steps.map((step, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <Chip label={step.priority} color={PRIORITY_COLORS[step.priority] || 'default'} size="small" />
                    {step.deadline_sensitive && (
                      <Chip label="Deadline Sensitive" color="error" size="small" variant="outlined" />
                    )}
                  </Box>
                  <Typography fontWeight={600}>{step.action}</Typography>
                  {step.rationale && (
                    <Typography variant="body2" color="text.secondary">{step.rationale}</Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

const UploadStep = ({ appeal, onNext, onUpdate, onError, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    denial: null,
    claim: null,
    supporting: []
  });
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patientName, setPatientName] = useState('');
  const [payerName, setPayerName] = useState('');
  const [newPatientDialogOpen, setNewPatientDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [extractedData, setExtractedData] = useState({
    patient_name: '',
    claim_number: '',
    payer_name: '',
    payer_id: '',
    denial_code: '',
    denial_text: '',
    provider_phone: '',
    provider_address: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [documents, setDocuments] = useState([]);
  const extractPollRef = useRef(null);

  // Denial analysis state
  const [denialAnalysis, setDenialAnalysis] = useState(null);
  const [analysisSubmitting, setAnalysisSubmitting] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const analysisPollRef = useRef(null);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (appeal) {
      setSelectedPatient(appeal.patient || '');

      let storedPatientName = '';
      let storedPayerName = '';
      try {
        storedPatientName = localStorage.getItem(`appeal-${appeal.id}-patient-name`) || '';
        storedPayerName = localStorage.getItem(`appeal-${appeal.id}-payer-name`) || '';
      } catch (err) {
        console.warn('Failed to read from localStorage:', err);
      }

      setPatientName(storedPatientName || appeal.patient_name || '');
      setPayerName(storedPayerName || appeal.payer_name || '');

      if (appeal.documents && appeal.documents.length > 0) {
        const files = { denial: null, claim: null, supporting: [] };
        appeal.documents.forEach(doc => {
          const docData = {
            id: doc.id,
            filename: doc.uploaded_document_info?.original_filename || 'Unknown file',
            document_type: doc.document_type
          };
          if (doc.document_type === 'denial') files.denial = docData;
          else if (doc.document_type === 'claim') files.claim = docData;
          else if (doc.document_type === 'supporting') files.supporting.push(docData);
        });
        setUploadedFiles(files);
        setDocuments(appeal.documents);

        // Check if extraction already done
        const hasOcrResults = appeal.documents.some(doc =>
          doc.ocr_status === 'success' || Object.keys(doc.extraction_metadata || {}).length > 0
        );
        if (hasOcrResults) {
          setExtractionComplete(true);
          setExtractedData({
            patient_name: appeal.patient_name || '',
            claim_number: appeal.claim_number || '',
            payer_name: appeal.payer_name || '',
            payer_id: appeal.payer_id || '',
            denial_code: appeal.denial_code || '',
            denial_text: appeal.denial_text || '',
            provider_phone: appeal.provider_phone || '',
            provider_address: appeal.provider_address || '',
          });
        }
      }
    }
  }, [appeal]);

  useEffect(() => {
    if (appeal?.id && patientName) {
      try {
        localStorage.setItem(`appeal-${appeal.id}-patient-name`, patientName); // eslint-disable-line no-restricted-properties
      } catch (err) {
        console.warn('Failed to save patient name:', err);
      }
    }
  }, [appeal?.id, patientName]);

  useEffect(() => {
    if (appeal?.id && payerName) {
      try {
        localStorage.setItem(`appeal-${appeal.id}-payer-name`, payerName); // eslint-disable-line no-restricted-properties
      } catch (err) {
        console.warn('Failed to save payer name:', err);
      }
    }
  }, [appeal?.id, payerName]);

  // Cleanup polls on unmount
  useEffect(() => {
    return () => {
      if (extractPollRef.current) clearInterval(extractPollRef.current);
      if (analysisPollRef.current) clearInterval(analysisPollRef.current);
    };
  }, []);

  const loadPatients = async () => {
    try {
      const response = await api.get('/patients/');
      const patientsData = response.data.data || response.data.results || response.data;
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatients([]);
    }
  };

  const handlePatientCreated = (createdPatient) => {
    loadPatients();
    setSelectedPatient(createdPatient.patient_id);
    setPatientName(`${createdPatient.first_name} ${createdPatient.last_name}`);
    onSuccess('Patient created successfully!');
  };

  const uploadFile = async (file, documentType) => {
    if (!appeal?.id) throw new Error('Appeal not initialized');
    const formData = new FormData();
    if (documentType === 'denial') formData.append('denial_letter', file);
    else if (documentType === 'claim') formData.append('original_claim', file);
    else if (documentType === 'supporting') formData.append('supporting_docs', file);
    const response = await api.post(`/documents/minimal-appeals/${appeal.id}/uploads/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  const handleFileUpload = async (files, documentType) => {
    if (!appeal?.id) { onError('Appeal not initialized. Please refresh the page.'); return; }
    setUploading(true);
    onError(null);
    try {
      for (const file of files) {
        const result = await uploadFile(file, documentType);
        setUploadedFiles(prev => {
          const newState = { ...prev };
          const uploadedDoc = result.uploaded_documents?.[0];
          if (!uploadedDoc) return prev;
          if (documentType === 'supporting') {
            newState.supporting = [...prev.supporting, { id: uploadedDoc.id, filename: file.name, document_type: documentType }];
          } else {
            newState[documentType] = { id: uploadedDoc.id, filename: file.name, document_type: documentType };
          }
          return newState;
        });
      }
      onSuccess(`${files.length} file(s) uploaded successfully!`);
      const updatedAppeal = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
      onUpdate(updatedAppeal.data);
      setDocuments(updatedAppeal.data.documents || []);
    } catch (err) {
      onError(err.response?.data?.error || err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = (fileId, documentType) => {
    setUploadedFiles(prev => {
      const newState = { ...prev };
      if (documentType === 'supporting') {
        newState.supporting = prev.supporting.filter(f => f.id !== fileId);
      } else {
        newState[documentType] = null;
      }
      return newState;
    });
    onSuccess('File removed');
  };

  // — Extraction —
  const startExtraction = async (force = false) => {
    if (!appeal || !documents.length) { onError('No documents found to extract from'); return; }
    setExtracting(true);
    onError(null);
    try {
      await api.post(`/documents/minimal-appeals/${appeal.id}/extract/`, { force });
      onSuccess('Extraction started. Processing documents...');
      if (extractPollRef.current) clearInterval(extractPollRef.current);
      extractPollRef.current = setInterval(checkExtractionStatus, 3000);
    } catch (err) {
      setExtracting(false);
      onError(err.response?.data?.error || 'Failed to start extraction');
    }
  };

  const checkExtractionStatus = async () => {
    try {
      const response = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
      const updatedAppeal = response.data;
      onUpdate(updatedAppeal);
      setDocuments(updatedAppeal.documents || []);

      const allDocs = updatedAppeal.documents || [];
      const completedDocs = allDocs.filter(doc => doc.ocr_status === 'success' || doc.ocr_status === 'failed');

      if (completedDocs.length === allDocs.length && allDocs.length > 0) {
        clearInterval(extractPollRef.current);
        extractPollRef.current = null;
        setExtracting(false);
        setExtractionComplete(true);

        const merged = { ...extractedData };
        allDocs.forEach(doc => {
          const extracted = doc.extraction_metadata?.extracted_data || {};
          Object.keys(extracted).forEach(key => {
            if (extracted[key] && !merged[key]) merged[key] = extracted[key];
          });
        });
        setExtractedData(merged);
        onSuccess('Extraction complete! Review the information below.');
      }
    } catch (err) {
      console.error('Extraction polling error:', err);
    }
  };

  const saveExtractedData = async () => {
    try {
      const response = await api.patch(`/documents/minimal-appeals/${appeal.id}/metadata/`, extractedData);
      onUpdate(response.data);
      setEditMode(false);
      onSuccess('Information updated successfully!');
    } catch (err) {
      onError('Failed to save extracted information');
    }
  };

  // — Denial Analysis —
  const handleRunDenialAnalysis = async () => {
    setAnalysisError(null);
    setAnalysisSubmitting(true);
    try {
      const createResp = await createDenialAnalysisFromAppeal(appeal.id);
      const analysis = createResp.data;
      setDenialAnalysis(analysis);
      await submitAnalysis(analysis.id);
      if (analysisPollRef.current) clearInterval(analysisPollRef.current);
      analysisPollRef.current = setInterval(() => pollAnalysis(analysis.id), 5000);
    } catch (err) {
      setAnalysisError(err.response?.data?.error || 'Failed to start denial analysis');
      setAnalysisSubmitting(false);
    }
  };

  const pollAnalysis = async (analysisId) => {
    try {
      const response = await getDenialAnalysis(analysisId);
      const updated = response.data;
      setDenialAnalysis(updated);
      if (updated.status === 'complete') {
        clearInterval(analysisPollRef.current);
        analysisPollRef.current = null;
        setAnalysisSubmitting(false);
        setAnalysisComplete(true);
      } else if (updated.status === 'failed') {
        clearInterval(analysisPollRef.current);
        analysisPollRef.current = null;
        setAnalysisSubmitting(false);
        setAnalysisError('Analysis failed. Please try again.');
      }
    } catch (err) {
      console.error('Analysis polling error:', err);
    }
  };

  const handleMetadataUpdate = async () => {
    if (!appeal?.id) return;
    const updateData = {};
    if (selectedPatient) updateData.patient = selectedPatient;
    if (patientName) updateData.patient_name = patientName;
    if (payerName) updateData.payer_name = payerName;
    if (Object.keys(updateData).length > 0) {
      try {
        const response = await api.patch(`/documents/minimal-appeals/${appeal.id}/`, updateData);
        onUpdate(response.data);
        try {
          localStorage.removeItem(`appeal-${appeal.id}-patient-name`);
          localStorage.removeItem(`appeal-${appeal.id}-payer-name`);
        } catch (err) { /* ignore */ }
      } catch (err) {
        console.error('Failed to update appeal information:', err);
        onError('Failed to update appeal information');
      }
    }
  };

  const handleNext = async () => {
    if (!appeal?.id) { onError('Appeal not initialized. Please refresh the page.'); return; }
    await handleMetadataUpdate();
    if (editMode) await saveExtractedData();
    const hasFiles = uploadedFiles.denial || uploadedFiles.claim || uploadedFiles.supporting.length > 0;
    if (!hasFiles) { onError('Please upload at least one document before proceeding'); return; }
    if (!patientName.trim()) { onError('Please enter the patient name before proceeding'); return; }
    if (!extractionComplete) { onError('Please run information extraction before proceeding'); return; }
    try {
      const updatedAppeal = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
      onUpdate(updatedAppeal.data);
    } catch (err) {
      console.error('Failed to refresh appeal data:', err);
    }
    onNext();
  };

  const DropzoneArea = ({ documentType, title, description, multiple = false }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: (files) => handleFileUpload(files, documentType),
      accept: {
        'application/pdf': ['.pdf'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg']
      },
      multiple,
      disabled: uploading || !appeal?.id
    });

    const hasFile = documentType === 'supporting'
      ? uploadedFiles.supporting.length > 0
      : uploadedFiles[documentType] !== null;

    return (
      <Paper
        {...getRootProps()}
        elevation={1}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : hasFile ? 'success.main' : 'grey.300',
          borderRadius: 2, p: 3, textAlign: 'center',
          cursor: uploading || !appeal?.id ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : hasFile ? 'success.light' : 'background.paper',
          opacity: uploading || !appeal?.id ? 0.6 : 1,
          '&:hover': { bgcolor: uploading || !appeal?.id ? 'background.paper' : 'action.hover' },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon size={40} />
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>{description}</Typography>
        {hasFile && (
          <Chip
            label={documentType === 'supporting' ? `${uploadedFiles.supporting.length} files` : '1 file uploaded'}
            color="success" size="small" sx={{ mt: 1 }}
          />
        )}
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          PDF, DOCX, PNG, JPG files accepted
        </Typography>
      </Paper>
    );
  };

  const renderUploadedFilesList = () => {
    const allFiles = [
      ...(uploadedFiles.denial ? [uploadedFiles.denial] : []),
      ...(uploadedFiles.claim ? [uploadedFiles.claim] : []),
      ...uploadedFiles.supporting
    ];
    if (!allFiles.length) return null;
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>Uploaded Files</Typography>
        <List>
          {allFiles.map((file, index) => (
            <ListItem key={`${file.id}-${index}`}>
              <DescriptionIcon size={16} />
              <ListItemText
                primary={file.filename || file.uploaded_document_info?.original_filename}
                secondary={`Type: ${file.document_type}`}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleDeleteFile(file.id, file.document_type)} size="small">
                  <DeleteIcon size={16} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const renderDocumentStatus = () => {
    if (!documents.length) return null;
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Document Processing Status</Typography>
          <Grid container spacing={2}>
            {documents.map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id || index}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" noWrap title={doc.uploaded_document_info?.original_filename}>
                    {doc.uploaded_document_info?.original_filename || `Document ${index + 1}`}
                  </Typography>
                  <Chip size="small" label={doc.document_type} variant="outlined" sx={{ mt: 1, mb: 1 }} />
                  <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                    {doc.ocr_status === 'success' && (
                      <>
                        <CheckCircleIcon size={16} color="var(--ab-approved)" />
                        <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>Processed</Typography>
                      </>
                    )}
                    {doc.ocr_status === 'failed' && (
                      <>
                        <ErrorIcon size={16} color="var(--ab-denied)" />
                        <Typography variant="caption" color="error.main" sx={{ ml: 0.5 }}>Failed</Typography>
                      </>
                    )}
                    {doc.ocr_status === 'pending' && extracting && (
                      <>
                        <CircularProgress size={16} />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>Processing...</Typography>
                      </>
                    )}
                    {doc.ocr_status === 'pending' && !extracting && (
                      <Typography variant="caption" color="text.secondary">Pending</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderExtractionSection = () => {
    const hasFiles = uploadedFiles.denial || uploadedFiles.claim || uploadedFiles.supporting.length > 0;
    if (!hasFiles) return null;

    if (extracting) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>Extracting Information...</Typography>
          <Typography variant="body2" color="textSecondary">
            Reading documents and extracting key information.
          </Typography>
          <LinearProgress sx={{ mt: 2, maxWidth: 400, mx: 'auto' }} />
        </Box>
      );
    }

    if (!extractionComplete) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <AutoFixHighIcon size={48} />
          <Typography variant="h6" gutterBottom>Extract Information from Documents</Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Our AI will identify patient names, claim numbers, denial reasons, and more.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AutoFixHighIcon size={16} />} onClick={() => startExtraction(false)} sx={{ mr: 2 }}>
            Start Extraction
          </Button>
          <Button variant="outlined" size="large" onClick={() => startExtraction(true)}>
            Force Re-extract
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {renderDocumentStatus()}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Extracted Information</Typography>
              <Button
                startIcon={<EditIcon size={16} />}
                onClick={() => setEditMode(!editMode)}
                variant={editMode ? 'contained' : 'outlined'}
                size="small"
              >
                {editMode ? 'View Mode' : 'Edit Mode'}
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Patient Name" value={extractedData.patient_name}
                  onChange={(e) => setExtractedData(p => ({ ...p, patient_name: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Claim Number" value={extractedData.claim_number}
                  onChange={(e) => setExtractedData(p => ({ ...p, claim_number: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Insurance Payer" value={extractedData.payer_name}
                  onChange={(e) => setExtractedData(p => ({ ...p, payer_name: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Payer ID" value={extractedData.payer_id}
                  onChange={(e) => setExtractedData(p => ({ ...p, payer_id: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Denial Code" value={extractedData.denial_code}
                  onChange={(e) => setExtractedData(p => ({ ...p, denial_code: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Denial Text/Reason"
                  value={extractedData.denial_text}
                  onChange={(e) => setExtractedData(p => ({ ...p, denial_text: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'}
                  placeholder="Enter the reason for denial as stated in the denial letter" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Provider Phone" value={extractedData.provider_phone}
                  onChange={(e) => setExtractedData(p => ({ ...p, provider_phone: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Provider Address"
                  value={extractedData.provider_address}
                  onChange={(e) => setExtractedData(p => ({ ...p, provider_address: e.target.value }))}
                  disabled={!editMode} variant={editMode ? 'outlined' : 'filled'} />
              </Grid>
            </Grid>
            {editMode && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={saveExtractedData}>Save Changes</Button>
                <Button variant="outlined" onClick={() => setEditMode(false)}>Cancel</Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderDenialAnalysisSection = () => {
    if (!extractionComplete) return null;

    // Need a denial letter for analysis
    const hasDenialLetter = uploadedFiles.denial !== null;
    if (!hasDenialLetter) return null;

    // Check if denial letter OCR is done
    const denialDocOcrDone = documents.some(
      doc => doc.document_type === 'denial' && doc.ocr_status === 'success'
    );
    if (!denialDocOcrDone) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: 'primary.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">Denial Letter Analysis</Typography>
            <Typography variant="body2" color="text.secondary">
              Get a structured breakdown: denial type, codes, plain-English explanation, and next steps.
            </Typography>
          </Box>
          {!analysisSubmitting && !analysisComplete && (
            <Button variant="outlined" startIcon={<AutoFixHighIcon size={16} />} onClick={handleRunDenialAnalysis}>
              Analyze Denial Letter
            </Button>
          )}
        </Box>

        {analysisError && <Alert severity="error" sx={{ mb: 2 }}>{analysisError}</Alert>}

        {analysisSubmitting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Analyzing denial letter with AI...</Typography>
          </Box>
        )}

        {analysisComplete && denialAnalysis?.result && (
          <InlineDenialResults result={denialAnalysis.result} />
        )}

        {analysisComplete && !denialAnalysis?.result && (
          <Alert severity="warning">Analysis complete but no results returned.</Alert>
        )}
      </Paper>
    );
  };

  if (!appeal || !appeal.id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading appeal data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Upload & Extract Information
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Upload your denial letter and documents. We'll extract key information and optionally run
        a full denial analysis before you generate your appeal.
      </Typography>

      {/* Appeal Information */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Appeal Information</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <InputLabel>Select Patient (Optional)</InputLabel>
                  <Select value={selectedPatient} label="Select Patient (Optional)"
                    onChange={(e) => setSelectedPatient(e.target.value)}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {patients.map((patient) => (
                      <MenuItem key={patient.patient_id} value={patient.patient_id}>
                        {patient.first_name} {patient.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Button variant="outlined" startIcon={<PersonAddIcon size={16} />}
                  onClick={() => setNewPatientDialogOpen(true)} fullWidth size="small">
                  New Patient
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Patient Name *" value={patientName}
              onChange={(e) => setPatientName(e.target.value)} placeholder="Enter patient's full name" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Insurance Payer" value={payerName}
              onChange={(e) => setPayerName(e.target.value)} placeholder="e.g., Blue Cross Blue Shield" />
          </Grid>
        </Grid>
      </Paper>

      {uploading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>Uploading files...</Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Upload Areas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <DropzoneArea documentType="denial" title="Denial Letter"
            description="Upload the insurance denial letter (required for best results)" />
        </Grid>
        <Grid item xs={12} md={4}>
          <DropzoneArea documentType="claim" title="Original Claim"
            description="Upload the original claim document" />
        </Grid>
        <Grid item xs={12} md={4}>
          <DropzoneArea documentType="supporting" title="Supporting Documents"
            description="Upload treatment notes, medical records, etc." multiple={true} />
        </Grid>
      </Grid>

      {renderUploadedFilesList()}

      {/* Extraction Section */}
      <Divider sx={{ my: 3 }} />
      {renderExtractionSection()}

      {/* Denial Analysis Section */}
      {renderDenialAnalysisSection()}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={uploading || !extractionComplete}
          size="large"
        >
          Next: Generate Appeal
        </Button>
      </Box>

      <PatientFormDialog
        open={newPatientDialogOpen}
        onClose={() => setNewPatientDialogOpen(false)}
        onPatientCreated={handlePatientCreated}
        loading={loading}
        onLoadingChange={setLoading}
      />
    </Box>
  );
};

export default UploadStep;
